// src/components/admin/AdminCalendar.js
import React, { useState, useEffect } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  User,
  Car,
  MapPin,
  Ban,
  X,
  Check
} from 'lucide-react';
import AdminService from '../../services/AdminService';

const TIME_SLOTS = [
  '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
  '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'
];

const AdminCalendar = ({ isMobile }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'week'
  const [blockedDates, setBlockedDates] = useState({});
  const [showBlockPanel, setShowBlockPanel] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Subscribe to all bookings in real-time
    const unsubBookings = AdminService.subscribeToAllBookings((updatedBookings) => {
      setBookings(updatedBookings);
      setLoading(false);
    });

    // Subscribe to blocked dates in real-time
    const unsubBlocked = AdminService.subscribeToBlockedDates((blocked) => {
      setBlockedDates(blocked);
    });

    return () => {
      unsubBookings();
      unsubBlocked();
    };
  }, []);

  // Helper: date → "YYYY-MM-DD" key
  const toDateKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Is a specific date fully blocked?
  const isDayBlocked = (date) => {
    const key = toDateKey(date);
    return blockedDates[key]?.allDay === true;
  };

  // Get blocked time slots for a date (empty array if none or allDay)
  const getBlockedTimes = (date) => {
    const key = toDateKey(date);
    const entry = blockedDates[key];
    if (!entry) return [];
    if (entry.allDay) return TIME_SLOTS; // all blocked
    return entry.times || [];
  };

  // Toggle blocking the entire selected day
  const toggleBlockDay = async () => {
    if (!selectedDate) return;
    const key = toDateKey(selectedDate);
    setSaving(true);
    try {
      if (isDayBlocked(selectedDate)) {
        await AdminService.unblockDate(key);
      } else {
        await AdminService.setBlockedDate(key, { allDay: true });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Toggle a single time slot for the selected day
  const toggleBlockTime = async (time) => {
    if (!selectedDate) return;
    const key = toDateKey(selectedDate);
    const entry = blockedDates[key];
    setSaving(true);
    try {
      const currentTimes = (entry && !entry.allDay && entry.times) ? [...entry.times] : [];
      if (currentTimes.includes(time)) {
        const updated = currentTimes.filter(t => t !== time);
        if (updated.length === 0) {
          await AdminService.unblockDate(key);
        } else {
          await AdminService.setBlockedDate(key, { times: updated });
        }
      } else {
        const updated = [...currentTimes, time];
        if (updated.length === TIME_SLOTS.length) {
          await AdminService.setBlockedDate(key, { allDay: true });
        } else {
          await AdminService.setBlockedDate(key, { times: updated });
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Clear all blocks for selected day
  const clearBlocksForDay = async () => {
    if (!selectedDate) return;
    setSaving(true);
    try {
      await AdminService.unblockDate(toDateKey(selectedDate));
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'bg-green-500';
      case 'pending':
        return 'bg-yellow-500';
      case 'completed':
        return 'bg-blue-500';
      case 'cancelled':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Get bookings for a specific date
  const getBookingsForDate = (date) => {
    const dateString = date.toDateString();
    return bookings.filter(booking => {
      if (!booking.schedule?.date) return false;
      const bookingDate = new Date(booking.schedule.date);
      return bookingDate.toDateString() === dateString;
    });
  };

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDay = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }

    return days;
  };

  // Navigate months
  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  // Navigate to today
  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(null);
  };

  const calendarDays = generateCalendarDays();
  const today = new Date();
  const monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-7 gap-2">
            {[...Array(42)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Calendar View</h2>
          <button
            onClick={goToToday}
            className="px-4 py-2 bg-[#3A98B9] text-white rounded-lg hover:bg-[#2A87A8] text-sm"
          >
            Today
          </button>
        </div>

        {/* Calendar Navigation */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <h3 className="text-lg font-medium">{monthYear}</h3>
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          <div className="text-sm text-gray-600">
            {bookings.length} total bookings
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {/* Calendar Header */}
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="px-4 py-3 text-center text-sm font-medium text-gray-700">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Body */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, index) => {
            const isCurrentMonth = day.getMonth() === currentDate.getMonth();
            const isToday = day.toDateString() === today.toDateString();
            const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString();
            const dayBookings = getBookingsForDate(day);
            const dayBlocked = isDayBlocked(day);
            const blockedTimes = getBlockedTimes(day);
            const hasPartialBlock = !dayBlocked && blockedTimes.length > 0;

            return (
              <div
                key={index}
                className={`min-h-[120px] border-r border-b border-gray-200 p-2 cursor-pointer hover:bg-gray-50 ${
                  !isCurrentMonth ? 'bg-gray-50 text-gray-400' : ''
                } ${isSelected ? 'bg-blue-50' : ''} ${
                  dayBlocked ? 'bg-red-50' : ''
                }`}
                onClick={() => { setSelectedDate(day); setShowBlockPanel(false); }}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className={`text-sm font-medium ${
                    isToday ? 'bg-[#3A98B9] text-white rounded-full w-6 h-6 flex items-center justify-center' : ''
                  }`}>
                    {day.getDate()}
                  </div>
                  {dayBlocked && (
                    <Ban className="h-3.5 w-3.5 text-red-500" />
                  )}
                  {hasPartialBlock && (
                    <span className="text-[10px] font-medium text-orange-600 bg-orange-100 px-1 rounded">
                      {blockedTimes.length} blocked
                    </span>
                  )}
                </div>

                {/* Bookings for this day */}
                <div className="space-y-1">
                  {dayBookings.slice(0, 3).map((booking) => (
                    <div
                      key={booking.bookingId}
                      className={`text-xs p-1 rounded text-white truncate ${getStatusColor(booking.status)}`}
                      title={`${booking.service?.title} - ${booking.userName} - ${booking.schedule?.time}`}
                    >
                      <div className="font-medium truncate">{booking.service?.title}</div>
                      <div className="truncate">{booking.schedule?.time}</div>
                    </div>
                  ))}
                  {dayBookings.length > 3 && (
                    <div className="text-xs text-gray-500 pl-1">
                      +{dayBookings.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Date Details */}
      {selectedDate && (
        <div className="mt-6 bg-white border border-gray-200 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
            <h3 className="text-lg font-medium">
              Bookings for {selectedDate.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </h3>
            <button
              onClick={() => setShowBlockPanel(!showBlockPanel)}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center ${
                showBlockPanel
                  ? 'bg-gray-200 text-gray-700'
                  : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
              }`}
            >
              <Ban className="h-4 w-4 mr-2" />
              {showBlockPanel ? 'Hide Availability' : 'Manage Availability'}
            </button>
          </div>

          {/* ── Block / Availability Panel ── */}
          {showBlockPanel && (
            <div className="px-6 py-5 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-800">
                  Availability — {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </h4>
                {(isDayBlocked(selectedDate) || getBlockedTimes(selectedDate).length > 0) && (
                  <button
                    onClick={clearBlocksForDay}
                    disabled={saving}
                    className="text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-1 rounded-lg hover:bg-green-100 flex items-center disabled:opacity-50"
                  >
                    <Check className="h-3.5 w-3.5 mr-1" />
                    Mark All Available
                  </button>
                )}
              </div>

              {/* Block entire day toggle */}
              <button
                onClick={toggleBlockDay}
                disabled={saving}
                className={`w-full mb-4 px-4 py-3 rounded-lg text-sm font-medium flex items-center justify-center border transition-all disabled:opacity-50 ${
                  isDayBlocked(selectedDate)
                    ? 'bg-red-100 border-red-300 text-red-800'
                    : 'bg-white border-gray-200 text-gray-700 hover:border-red-300 hover:bg-red-50'
                }`}
              >
                <Ban className="h-4 w-4 mr-2" />
                {isDayBlocked(selectedDate) ? 'Day is Blocked — Click to Unblock' : 'Block Entire Day'}
              </button>

              {/* Individual time slot toggles */}
              {!isDayBlocked(selectedDate) && (
                <>
                  <p className="text-sm text-gray-600 mb-3">Or block individual time slots:</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {TIME_SLOTS.map((time) => {
                      const isBlocked = getBlockedTimes(selectedDate).includes(time);
                      return (
                        <button
                          key={time}
                          onClick={() => toggleBlockTime(time)}
                          disabled={saving}
                          className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all disabled:opacity-50 flex items-center justify-center ${
                            isBlocked
                              ? 'bg-red-100 border-red-300 text-red-800'
                              : 'bg-white border-gray-200 text-gray-700 hover:border-red-300 hover:bg-red-50'
                          }`}
                        >
                          {isBlocked && <X className="h-3.5 w-3.5 mr-1" />}
                          {time}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="divide-y divide-gray-200">
            {getBookingsForDate(selectedDate).length > 0 ? (
              getBookingsForDate(selectedDate).map((booking) => (
                <div key={booking.bookingId} className="px-6 py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className={`w-3 h-3 rounded-full ${getStatusColor(booking.status)}`}></span>
                        <span className="font-medium">{booking.service?.title}</span>
                        <span className="text-sm text-gray-500">
                          #{booking.bookingId.slice(-6).toUpperCase()}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span>{booking.schedule?.time}</span>
                        </div>

                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span>{booking.userName}</span>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Car className="h-4 w-4 text-gray-400" />
                          <span>
                            {booking.vehicle?.year} {booking.vehicle?.make} {booking.vehicle?.model}
                          </span>
                        </div>

                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span>{booking.vehicle?.location || 'Not specified'}</span>
                        </div>
                      </div>

                      <div className="mt-2 text-sm text-gray-600">
                        {booking.userEmail} • {formatCurrency(booking.payment?.total || booking.service?.price)}
                        {booking.bookingType === 'guest' && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                            Guest
                          </span>
                        )}
                      </div>
                    </div>

                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status).replace('bg-', 'bg-').replace('-500', '-100')} ${getStatusColor(booking.status).replace('bg-', 'text-').replace('-500', '-800')}`}>
                      {booking.status || 'Pending'}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-8 text-center text-gray-500">
                <CalendarIcon className="h-8 w-8 mx-auto mb-2" />
                <p>No bookings scheduled for this date</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCalendar;